/**
 * Inline SVG icons lifted from `planning/UI design/Docsight App.dc.html`.
 *
 * They are decorative: the accessible name always comes from the surrounding
 * link/button text, so every icon is `aria-hidden` and out of the tab order.
 */

export interface IconProps {
  /** Square size in px. Defaults to the design's 16px nav icon. */
  size?: number;
  className?: string;
}

const base = (size: number, className?: string) => ({
  width: size,
  height: size,
  viewBox: '0 0 16 16',
  'aria-hidden': true,
  focusable: false,
  ...(className === undefined ? {} : { className }),
});

const stroked = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 } as const;

export function DashboardIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.5" />
      <rect x="9" y="1.5" width="5.5" height="5.5" rx="1.5" />
      <rect x="1.5" y="9" width="5.5" height="5.5" rx="1.5" />
      <rect x="9" y="9" width="5.5" height="5.5" rx="1.5" />
    </svg>
  );
}

export function DocumentsIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <rect x="3" y="1.5" width="10" height="13" rx="1.5" />
      <line x1="5.5" y1="5.5" x2="10.5" y2="5.5" />
      <line x1="5.5" y1="8.5" x2="10.5" y2="8.5" />
      <line x1="5.5" y1="11.5" x2="8.5" y2="11.5" />
    </svg>
  );
}

export function ReviewIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <circle cx="8" cy="8" r="6.5" />
      <polyline points="5.5 8 7.3 9.8 10.8 6.3" />
    </svg>
  );
}

export function AutomationIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} fill="currentColor">
      <path d="M8.8 1.5 L3.5 9 L7.4 9 L6.6 14.5 L12.5 7 L8.2 7 Z" />
    </svg>
  );
}

export function TaxonomyIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <circle cx="8" cy="3.2" r="1.8" />
      <circle cx="3.5" cy="12.2" r="1.8" />
      <circle cx="12.5" cy="12.2" r="1.8" />
      <line x1="7" y1="4.8" x2="4.2" y2="10.6" />
      <line x1="9" y1="4.8" x2="11.8" y2="10.6" />
    </svg>
  );
}

export function SettingsIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <line x1="1.5" y1="3.5" x2="14.5" y2="3.5" />
      <line x1="1.5" y1="8" x2="14.5" y2="8" />
      <line x1="1.5" y1="12.5" x2="14.5" y2="12.5" />
      <circle cx="10" cy="3.5" r="1.8" fill="var(--card)" />
      <circle cx="5" cy="8" r="1.8" fill="var(--card)" />
      <circle cx="11.5" cy="12.5" r="1.8" fill="var(--card)" />
    </svg>
  );
}

export function PartnerIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <rect x="1.5" y="9" width="5.5" height="5.5" rx="1" />
      <rect x="9" y="9" width="5.5" height="5.5" rx="1" />
      <rect x="5.25" y="1.5" width="5.5" height="5.5" rx="1" />
    </svg>
  );
}

export function SystemAdminIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <rect x="3.5" y="7" width="9" height="7" rx="1.5" />
      <path d="M5.5 7 L5.5 5 a2.5 2.5 0 0 1 5 0 L10.5 7" />
    </svg>
  );
}

export function HealthIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <polyline points="1.5 9 4.5 9 6.5 4.5 9.5 12.5 11.5 9 14.5 9" />
    </svg>
  );
}

export function VisibilityIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <path d="M1.5 8 C3.5 4 6 3 8 3 C10 3 12.5 4 14.5 8 C12.5 12 10 13 8 13 C6 13 3.5 12 1.5 8 Z" />
      <circle cx="8" cy="8" r="2.1" />
    </svg>
  );
}

export function DesktopAppIcon({ size = 16, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <rect x="1.5" y="2.5" width="13" height="9" rx="1.5" />
      <line x1="5.5" y1="14" x2="10.5" y2="14" />
      <polyline points="6 6 8 8 10 6" />
    </svg>
  );
}

/** Platform marks for the desktop uploader's download cards. Filled, not
 * stroked: both are solid silhouettes, and an outlined Apple mark reads as a
 * different logo. */
export function WindowsIcon({ size = 14, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} fill="currentColor">
      <path d="M1.5 3.1 6.8 2.4v5.1H1.5zM7.8 2.2 14.5 1.3v6.2H7.8zM1.5 8.5h5.3v5.1L1.5 12.9zM7.8 8.5h6.7v6.2L7.8 13.8z" />
    </svg>
  );
}

export function MacosIcon({ size = 14, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} fill="currentColor">
      <path d="M10.6 1.2c.1.9-.25 1.75-.8 2.35-.56.62-1.42 1.08-2.24 1.02-.11-.86.3-1.76.83-2.32.58-.63 1.55-1.09 2.21-1.05zM13.4 11.6c-.35.82-.52 1.18-.97 1.9-.63 1-1.52 2.26-2.62 2.27-.98.01-1.23-.64-2.56-.63-1.33 0-1.6.64-2.58.63-1.1-.01-1.94-1.14-2.57-2.14C.32 10.83.13 7.55 1.22 5.8c.77-1.24 1.99-1.97 3.13-1.97 1.17 0 1.9.64 2.86.64.94 0 1.51-.64 2.86-.64 1.02 0 2.1.56 2.87 1.52-2.52 1.38-2.11 4.98.46 6.25z" />
    </svg>
  );
}

export function CalendarIcon({ size = 13, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} fill="none" stroke="var(--t3)" strokeWidth={1.5}>
      <rect x="1.5" y="2.5" width="13" height="12" rx="2" />
      <line x1="1.5" y1="6" x2="14.5" y2="6" />
      <line x1="5" y1="1" x2="5" y2="4" />
      <line x1="11" y1="1" x2="11" y2="4" />
    </svg>
  );
}

export function SearchIcon({ size = 13, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} fill="none" stroke="currentColor" strokeWidth={1.6}>
      <circle cx="7" cy="7" r="5" />
      <line x1="10.8" y1="10.8" x2="14" y2="14" />
    </svg>
  );
}

export function BellIcon({ size = 15, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <path d="M8 2 a4 4 0 0 1 4 4 c0 3.5 1.5 4.5 1.5 4.5 L2.5 10.5 c0 0 1.5-1 1.5-4.5 a4 4 0 0 1 4-4 Z" />
      <path d="M6.6 13 a1.6 1.6 0 0 0 2.8 0" />
    </svg>
  );
}

export function UserIcon({ size = 15, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} {...stroked}>
      <circle cx="8" cy="5.5" r="2.75" />
      <path d="M2.75 14 a5.25 5.25 0 0 1 10.5 0" />
    </svg>
  );
}

export function SignInIcon({ size = 15, className }: IconProps): React.JSX.Element {
  return (
    <svg {...base(size, className)} fill="none" stroke="currentColor" strokeWidth={1.6}>
      <rect x="1.5" y="1.5" width="13" height="13" rx="2.5" />
      <path d="M5 8 L7 10 L11 5.5" />
    </svg>
  );
}
